/**
 * GroupedSelect Component
 *
 * A styled select dropdown with visual grouping, connecting lines,
 * sticky headers, and enforced scroll constraints.
 */

import { cn } from '@/shared/utils';
import * as SelectPrimitive from '@radix-ui/react-select';
import { useCallback, useMemo, useState } from 'react';
import { Icon, IconKey, getIconComponent } from '../icon';
import { GroupHeader } from './group-header';
import { GroupItem } from './group-item';
import type { GroupedSelectProps } from './types';

const DEFAULT_MAX_HEIGHT = 420;

export function GroupedSelect({
  id,
  value,
  onValueChange,
  groups,
  topLevelItems = [],
  placeholder = 'Select...',
  disabled = false,
  className,
  maxHeight = DEFAULT_MAX_HEIGHT,
  stickyHeaders = true,
  collapsible = false,
  'aria-label': ariaLabel
}: GroupedSelectProps) {
  // Track which groups/items are expanded (empty = all collapsed by default)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  // Find the selected item to display in the trigger
  const selectedItem = useMemo(() => {
    // Check top-level items first
    const topLevel = topLevelItems.find((item) => item.id === value);
    if (topLevel) return topLevel;

    // Check groups
    for (const group of groups) {
      const found = group.items.find((item) => item.id === value);
      if (found) return found;
    }
    return null;
  }, [value, topLevelItems, groups]);

  // Handle value changes - convert internal null placeholder back to null
  const handleValueChange = (newValue: string) => {
    onValueChange(newValue === '__null__' ? null : newValue);
  };

  // Use internal placeholder for null to avoid Radix issues with empty strings
  const selectValue = value ?? '__null__';

  const SelectedIconComponent = selectedItem?.icon ? getIconComponent(selectedItem.icon) : undefined;

  return (
    <SelectPrimitive.Root value={selectValue} onValueChange={handleValueChange} disabled={disabled}>
      <SelectPrimitive.Trigger
        id={id}
        aria-labelledby={id}
        aria-label={ariaLabel}
        className={cn(
          'flex h-9 w-full items-center justify-between rounded-md border border-border bg-input/30 dark:hover:bg-input/50 hover:cursor-pointer px-3 py-2 text-sm text-text ring-offset-background',
          'data-placeholder:text-muted-foreground',
          'focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-1',
          'disabled:cursor-not-allowed disabled:opacity-50',
          '[&>span]:line-clamp-1',
          className
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder}>
          {selectedItem ? (
            <span className='flex items-center gap-2'>
              {SelectedIconComponent && (
                <SelectedIconComponent
                  className='h-3.5 w-3.5 shrink-0'
                  style={selectedItem.color ? { color: selectedItem.color } : undefined}
                />
              )}
              <span className='truncate'>{selectedItem.label}</span>
            </span>
          ) : (
            placeholder
          )}
        </SelectPrimitive.Value>
        <SelectPrimitive.Icon asChild>
          <Icon icon={IconKey.ChevronDown} size={16} className='opacity-50' />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          className={cn(
            'relative z-[60] min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
            'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            'origin-[--radix-select-content-transform-origin]',
            'data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1'
          )}
          position='popper'
          sideOffset={4}
        >
          <SelectPrimitive.Viewport className='w-full min-w-(--radix-select-trigger-width) pt-1' style={{ maxHeight: `${maxHeight}px` }}>
            {/* Hidden sentinel so Radix always finds a matching item for the __null__ value */}
            <SelectPrimitive.Item value="__null__" className="hidden" aria-hidden="true">
              <SelectPrimitive.ItemText>{placeholder}</SelectPrimitive.ItemText>
            </SelectPrimitive.Item>

            {/* Top-level items (e.g., "All Categories", "Uncategorized") */}
            {topLevelItems.map((item, index) => (
              <div key={item.id} className='px-1'>
                {index > 0 && <SelectPrimitive.Separator className='-mx-1 my-1 h-px bg-muted' />}
                <GroupItem
                  item={item}
                  isSelected={item.id === value}
                  isInGroup={false}
                />
              </div>
            ))}

            {/* Separator between top-level and groups */}
            {topLevelItems.length > 0 && groups.length > 0 && <SelectPrimitive.Separator className='-mx-1 my-1 h-px bg-muted' />}

            {/* Groups */}
            {groups.map((group) => {
              const isExpanded = !collapsible || expandedIds.has(group.id);

              return (
                <SelectPrimitive.Group key={group.id}>
                  <GroupHeader
                    label={group.label}
                    icon={group.icon}
                    color={group.color}
                    isSticky={stickyHeaders}
                    collapsible={collapsible}
                    isExpanded={isExpanded}
                    onToggle={() => toggleGroup(group.id)}
                  />

                  {/* Items with connecting line */}
                  {isExpanded && (
                    <div className='relative ml-[15px] border-l-2 border-border pl-0 space-y-1 py-1 mx-1'>
                      {group.items.map((item, idx) => {
                        // When collapsible, check if this item is hidden by a collapsed ancestor
                        if (collapsible && item.depth != null && item.depth > 0) {
                          // Walk backwards to find the nearest ancestor with children at a lower depth
                          for (let i = idx - 1; i >= 0; i--) {
                            const prev = group.items[i];
                            if (prev.depth != null && prev.depth < item.depth && prev.hasChildren) {
                              if (!expandedIds.has(prev.id)) return null;
                              break;
                            }
                          }
                        }

                        return (
                          <GroupItem
                            key={item.id}
                            item={item}
                            isSelected={item.id === value}
                            isInGroup
                            isExpanded={item.hasChildren ? expandedIds.has(item.id) : undefined}
                            onToggleChildren={item.hasChildren && collapsible ? () => toggleGroup(item.id) : undefined}
                          />
                        );
                      })}
                    </div>
                  )}
                </SelectPrimitive.Group>
              );
            })}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
